import React, { useEffect, useState } from 'react';
import * as S from '../../../../../components/styles/modal-style';
import { Overlay, ErrorText, InfoText } from './QuickModal.style';
import CostManager, { CostItem } from '../../../../../components/CostManager';
import ApiService from '../../../../../services/ApiService';

interface QuickStageCostModalProps {
  isOpen: boolean;
  onClose: () => void;
  stageId: number;
  stageName: string;
  unitId: number;
  unitName: string;
  /** Serviço ainda sem markup — o campo aparece junto do custo. */
  needsMarkup: boolean;
  /** Pendência de markup apenas (custo já existe na unidade) — grava via PATCH. */
  markupOnly?: boolean;
  onSaved: (result: { cost?: number; markup?: number }) => void;
}

/**
 * Custos do serviço a partir da venda: mostra os já cadastrados (em leitura) e permite
 * adicionar novos por unidade — mesmo CostManager da tela de serviços. O endpoint é
 * append-only (unidade existente é rejeitada); markup só é enviado quando o serviço
 * ainda não tem (nunca sobrescreve).
 */
const QuickStageCostModal: React.FC<QuickStageCostModalProps> = ({
  isOpen, onClose, stageId, stageName, unitId, unitName, needsMarkup, markupOnly, onSaved,
}) => {
  const [costsList, setCostsList] = useState<CostItem[]>([]);
  const [existingUnitIds, setExistingUnitIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [markupInput, setMarkupInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || markupOnly) return;
    let active = true;
    setLoading(true);
    setError('');
    ApiService.steelOrders.getServices({ id: stageId })
      .then((res: any) => {
        if (!active) return;
        const existentes = (res.data?.services?.[0]?.costs || []).map((c: any) => ({
          cost: Number(c.cost),
          unitOfMeasurementId: Number(c.unitOfMeasurementId),
        }));
        setCostsList(existentes);
        setExistingUnitIds(existentes.map((c: CostItem) => c.unitOfMeasurementId));
      })
      .catch(() => {
        if (!active) return;
        setCostsList([]);
        setExistingUnitIds([]);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isOpen, markupOnly, stageId]);

  if (!isOpen) return null;

  const parseNum = (raw: string) => Number(String(raw).replace(',', '.'));
  const novos = costsList.filter(c => !existingUnitIds.includes(Number(c.unitOfMeasurementId)));

  const handleConfirm = async () => {
    setError('');
    const markup = markupInput !== '' ? parseNum(markupInput) : undefined;

    if (markupOnly) {
      if (markup === undefined || isNaN(markup)) {
        setError('Informe um markup válido.');
        return;
      }
      setSaving(true);
      try {
        await ApiService.services.update(stageId, { markup });
        onSaved({ markup });
        setMarkupInput('');
        onClose();
      } catch (e: any) {
        const message = e?.response?.data?.message;
        setError(Array.isArray(message) ? message.join(' ') : (message || 'Erro ao salvar. Tente novamente.'));
      } finally {
        setSaving(false);
      }
      return;
    }

    if (novos.length === 0) {
      setError('Adicione ao menos um custo novo.');
      return;
    }
    if (needsMarkup && markupInput !== '' && (markup === undefined || isNaN(markup))) {
      setError('Informe um markup válido.');
      return;
    }

    setSaving(true);
    try {
      for (let i = 0; i < novos.length; i++) {
        await ApiService.services.addStageCost(stageId, {
          cost: Number(novos[i].cost),
          unitOfMeasurementId: Number(novos[i].unitOfMeasurementId),
          ...(i === 0 && needsMarkup && markup !== undefined ? { markup } : {}),
        });
      }
      const costNaUnidadeDoItem = novos.find(c => Number(c.unitOfMeasurementId) === Number(unitId))?.cost;
      onSaved({
        ...(costNaUnidadeDoItem !== undefined ? { cost: Number(costNaUnidadeDoItem) } : {}),
        ...(needsMarkup && markup !== undefined ? { markup } : {}),
      });
      setMarkupInput('');
      onClose();
    } catch (e: any) {
      const message = e?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(' ') : (message || 'Erro ao salvar. Tente novamente.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <S.ContainerOfModal width={markupOnly ? '420px' : '560px'} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.Header>
          <S.HeaderTitle>{markupOnly ? 'Informar markup' : 'Custos do serviço'}</S.HeaderTitle>
        </S.Header>
        <InfoText>
          {markupOnly
            ? <>O serviço <strong>{stageName}</strong> tem custo em {unitName}, mas está sem markup no cadastro.</>
            : <><strong>{stageName}</strong> está sem custo em <strong>{unitName}</strong>. Os custos abaixo já estão no cadastro; adicione os que faltam — valem para as próximas vendas.</>}
        </InfoText>
        {!markupOnly && (
          loading
            ? <InfoText>Carregando custos cadastrados…</InfoText>
            : (
              <CostManager
                costs={costsList}
                onChange={setCostsList}
                lockedUnitIds={existingUnitIds}
              />
            )
        )}
        {(markupOnly || needsMarkup) && (
          <S.InputDiv>
            <S.Label>Markup (fator, ex: 1,3)</S.Label>
            <S.Input
              type="text"
              inputMode="decimal"
              value={markupInput}
              onChange={(e) => setMarkupInput(e.target.value)}
              placeholder="1,0"
              autoFocus={!!markupOnly}
            />
          </S.InputDiv>
        )}
        {error && <ErrorText>{error}</ErrorText>}
        <S.BottomDiv>
          <S.ButtonContainer>
            <S.Button type="button" onClick={onClose} disabled={saving}>Cancelar</S.Button>
            <S.Button type="button" onClick={handleConfirm} disabled={saving || (!markupOnly && novos.length === 0)}>
              {saving ? 'Salvando…' : markupOnly ? 'Salvar' : novos.length > 1 ? `Salvar ${novos.length} custos` : 'Salvar custo'}
            </S.Button>
          </S.ButtonContainer>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </Overlay>
  );
};

export default QuickStageCostModal;
